import React, { useEffect, useState } from 'react';
import { Building2, LoaderCircle } from 'lucide-react';
import { getStoredConfig, getSupabaseClient } from '../lib/supabase';
import { useApp } from '../context/AppContext';

interface Props { onOpenSupabaseModal: () => void }

export const AuthScreen: React.FC<Props> = ({ onOpenSupabaseModal }) => {
  const { login } = useApp();
  const isConfigured = getStoredConfig().isConfigured;
  const lineLoginConfigured = import.meta.env.VITE_LINE_OAUTH_ENABLED === 'true';
  const [register, setRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [phone, setPhone] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [recoveryMode, setRecoveryMode] = useState(false);

  useEffect(() => {
    const showRecoveryForm = () => {
      sessionStorage.setItem('narawadee_password_recovery', 'true');
      setRecoveryMode(true);
      setRegister(false);
      setNotice('ตั้งรหัสผ่านใหม่สำหรับบัญชีของคุณ');
    };
    const client = getSupabaseClient();
    const query = new URLSearchParams(window.location.search);
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const recoveryRequested = sessionStorage.getItem('narawadee_password_recovery') === 'true'
      || query.get('reset') === '1'
      || query.get('type') === 'recovery'
      || hash.get('type') === 'recovery';
    if (recoveryRequested && client) {
      void client.auth.getSession().then(({ data }) => {
        if (data.session) showRecoveryForm();
        else {
          sessionStorage.removeItem('narawadee_password_recovery');
          setError('ลิงก์ตั้งรหัสผ่านไม่ถูกต้องหรือหมดอายุ กรุณาขอลิงก์ใหม่');
        }
      });
    }
    const registrationError = sessionStorage.getItem('resident_registration_error');
    if (registrationError) {
      setError(registrationError);
      sessionStorage.removeItem('resident_registration_error');
    }
    if (!client) return;
    const { data: { subscription } } = client.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        showRecoveryForm();
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setNotice('');
    if (password.length < 8) { setError('รหัสผ่านต้องมีอย่างน้อย 8 ตัวอักษร'); return; }
    setBusy(true);
    try {
      const client = getSupabaseClient();
      if (!client) throw new Error('ยังไม่ได้ตั้งค่า Supabase กรุณาตั้งค่าการเชื่อมต่อก่อน');
      const normalizedEmail = email.trim().toLowerCase();
      if (recoveryMode) {
        if (password !== confirmPassword) throw new Error('รหัสผ่านใหม่ทั้งสองช่องไม่ตรงกัน');
        const { error: updateError } = await client.auth.updateUser({ password });
        if (updateError) throw updateError;
        setRecoveryMode(false);
        setPassword('');
        setConfirmPassword('');
        sessionStorage.removeItem('narawadee_password_recovery');
        window.history.replaceState({}, document.title, window.location.pathname);
        setNotice('เปลี่ยนรหัสผ่านเรียบร้อยแล้ว กำลังเข้าสู่ระบบ');
      } else if (register) {
        if (!fullName.trim() || !houseNumber.trim()) throw new Error('กรุณากรอกชื่อและเลขที่บ้าน');
        if (!inviteCode.trim()) throw new Error('กรุณากรอกรหัสเชิญจากนิติบุคคลหรือผู้ดูแล');
        const { data: inviteValid, error: inviteError } = await client.rpc('check_resident_invite', {
          p_code: inviteCode.trim().toUpperCase(), p_house_number: houseNumber.trim(),
        });
        if (inviteError) throw inviteError;
        if (!inviteValid) throw new Error('รหัสเชิญไม่ถูกต้อง หมดอายุ ถูกใช้แล้ว หรือเลขที่บ้านไม่ตรง');
        const { error: signUpError } = await client.auth.signUp({
          email: normalizedEmail,
          password,
          options: { data: { full_name: fullName.trim(), house_number: houseNumber.trim(), phone: phone.trim(), invite_code: inviteCode.trim().toUpperCase() } },
        });
        if (signUpError) throw signUpError;
        setNotice('สมัครเรียบร้อยแล้ว กรุณายืนยันอีเมลและรอผู้ดูแลอนุมัติบัญชีก่อนเข้าใช้งาน');
      } else {
        const { data: auth, error: signInError } = await client.auth.signInWithPassword({ email: normalizedEmail, password });
        if (signInError) throw signInError;
        if (!auth.user) throw new Error('ไม่พบผู้ใช้ที่เข้าสู่ระบบ');
        const { data: profile, error: profileError } = await client
          .from('profiles')
          .select('id,email,full_name,role,house_number,phone,department,avatar_url,created_at,account_status')
          .eq('auth_user_id', auth.user.id)
          .maybeSingle();
        if (profileError) throw profileError;
        if (!profile || profile.account_status !== 'active') {
          await client.auth.signOut();
          throw new Error('บัญชียังไม่ได้รับอนุมัติ หรือไม่มีโปรไฟล์ที่เปิดใช้งาน กรุณาติดต่อผู้ดูแล');
        }
        login({ ...profile, role: profile.role });
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'ทำรายการไม่สำเร็จ กรุณาลองอีกครั้ง');
    } finally {
      setBusy(false);
    }
  };

  const signInWithLine = async () => {
    setError(''); setNotice('');
    if (register && (!fullName.trim() || !houseNumber.trim() || !email.trim() || !inviteCode.trim())) {
      setError('กรอกชื่อ เลขที่บ้าน อีเมล และรหัสเชิญให้ครบก่อนสมัครด้วย LINE'); return;
    }
    const client = getSupabaseClient();
    if (!client) { setError('ยังไม่ได้ตั้งค่า Supabase'); return; }
    setBusy(true);
    try {
      if (register) sessionStorage.setItem('pending_line_registration', JSON.stringify({
          fullName: fullName.trim(), houseNumber: houseNumber.trim(), phone: phone.trim(),
          email: email.trim().toLowerCase(), inviteCode: inviteCode.trim().toUpperCase(),
        }));
      else sessionStorage.removeItem('pending_line_registration');
      const liff = window.liff;
      if (liff && (liff.isInLIFFClient || liff.isInClient?.())) {
        if (!liff.isLoggedIn()) {
          liff.login({ redirectUri: window.location.href });
          return;
        }
        const idToken = liff.getIDToken?.();
        if (!idToken) throw new Error('รับ LINE ID token ไม่ได้ กรุณาเปิด LIFF Login channel และลองใหม่');
        const { error: tokenError } = await client.auth.signInWithIdToken({ provider: 'custom:line', token: idToken });
        if (tokenError) throw tokenError;
      } else {
        const { error: oauthError } = await client.auth.signInWithOAuth({
          provider: 'custom:line',
          options: { redirectTo: window.location.origin },
        });
        if (oauthError) throw oauthError;
      }
    } catch (cause) {
      sessionStorage.removeItem('pending_line_registration');
      setError(cause instanceof Error ? cause.message : 'เริ่มเข้าสู่ระบบ LINE ไม่สำเร็จ');
      setBusy(false);
    }
  };

  const resetPassword = async () => {
    setError(''); setNotice('');
    const client = getSupabaseClient();
    if (!client) { setError('ยังไม่ได้ตั้งค่า Supabase'); return; }
    if (!email.trim()) { setError('กรอกอีเมลก่อนขอเปลี่ยนรหัสผ่าน'); return; }
    setBusy(true);
    try {
      const { error: resetError } = await client.auth.resetPasswordForEmail(email.trim().toLowerCase(), {
        redirectTo: 'https://main.narawadee-elegance.pages.dev/?reset=1',
      });
      if (resetError) setError(resetError.message);
      else setNotice('ส่งลิงก์เปลี่ยนรหัสผ่านไปยังอีเมลแล้ว');
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'ส่งคำขอไม่สำเร็จ กรุณาลองอีกครั้ง');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-10">
      <section className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700"><Building2 /></div>
          <h1 className="text-2xl font-bold text-blue-800">นราวดี เอเลแกนซ์</h1>
          <p className="mt-1 text-sm text-slate-500">ระบบบริการชุมชน</p>
        </div>
        {!isConfigured && <button type="button" onClick={onOpenSupabaseModal} className="mb-4 w-full rounded-lg border border-amber-300 bg-amber-50 p-3 text-left text-sm text-amber-900">ตั้งค่าการเชื่อมต่อฐานข้อมูล Supabase เพื่อเริ่มใช้งาน</button>}
        {error && <p role="alert" className="mb-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{error}</p>}
        {notice && <p role="status" className="mb-4 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">{notice}</p>}
        <form onSubmit={submit} className="space-y-4">
          {register && !recoveryMode && <>
            <label className="block text-sm text-slate-700">ชื่อ-นามสกุล<input required autoComplete="name" value={fullName} onChange={e => setFullName(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5" /></label>
            <label className="block text-sm text-slate-700">เลขที่บ้าน<input required value={houseNumber} onChange={e => setHouseNumber(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5" /></label>
            <label className="block text-sm text-slate-700">เบอร์โทรศัพท์<input autoComplete="tel" value={phone} onChange={e => setPhone(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5" /></label>
            <label className="block text-sm text-slate-700">รหัสเชิญจากนิติบุคคล/ผู้ดูแล<input required autoComplete="off" value={inviteCode} onChange={e => setInviteCode(e.target.value.toUpperCase())} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5 font-mono tracking-wider" /></label>
          </>}
          {!recoveryMode && <label className="block text-sm text-slate-700">อีเมล<input required type="email" autoComplete="email" value={email} onChange={e => setEmail(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5" /></label>}
          <label className="block text-sm text-slate-700">{recoveryMode ? 'รหัสผ่านใหม่' : register ? 'รหัสผ่าน (เฉพาะสมัครด้วยอีเมล)' : 'รหัสผ่าน'}<input required={!register || recoveryMode} minLength={8} type="password" autoComplete={register || recoveryMode ? 'new-password' : 'current-password'} value={password} onChange={e => setPassword(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5" /></label>
          {recoveryMode && <label className="block text-sm text-slate-700">ยืนยันรหัสผ่านใหม่<input required minLength={8} type="password" autoComplete="new-password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2.5" /></label>}
          {!register && !recoveryMode && <button type="button" onClick={resetPassword} disabled={busy} className="text-sm text-blue-700 hover:underline disabled:opacity-60">ลืมรหัสผ่าน?</button>}
          <button disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-4 py-3 font-semibold text-white hover:bg-blue-800 disabled:opacity-60">{busy && <LoaderCircle className="h-4 w-4 animate-spin" />}{recoveryMode ? 'บันทึกรหัสผ่านใหม่' : register ? 'สมัครบัญชีลูกบ้าน' : 'เข้าสู่ระบบ'}</button>
        </form>
        {!recoveryMode && lineLoginConfigured && <button type="button" disabled={busy} onClick={signInWithLine} className="mt-3 w-full rounded-lg border border-emerald-600 px-4 py-3 font-semibold text-emerald-800 hover:bg-emerald-50 disabled:opacity-60">{register ? 'สมัครด้วย LINE' : 'เข้าสู่ระบบด้วย LINE'}</button>}
        {!recoveryMode && <p className="mt-5 text-center text-sm text-slate-600">{register ? 'มีบัญชีแล้ว?' : 'ยังไม่มีบัญชีลูกบ้าน?'}{' '}<button type="button" onClick={() => { setRegister(!register); setError(''); setNotice(''); }} className="font-medium text-blue-700 hover:underline">{register ? 'เข้าสู่ระบบ' : 'สมัครบัญชี'}</button></p>}
        <p className="mt-3 text-center text-xs text-slate-500">บัญชีเจ้าหน้าที่และผู้ดูแลต้องได้รับการสร้างและกำหนดสิทธิ์โดยผู้ดูแลระบบ</p>
      </section>
    </main>
  );
};
